import { getTourAttributes } from "@common/contexts/Tour";
import Collapsible from "@common/controls/Collapsible/component";
import Hint from "@common/controls/Hint/component";
import Icon from "@common/controls/Icon/component";
import { FancyCheckbox, type FancyCheckboxNullIndicator } from "@common/controls/Inputs/Checkbox/component";
import { type GenericMap, type OptionItemDescription } from "@common/declarations";
import { type KeyboardNavigationMode } from "@common/hocs/keyboardNavigation";
import withDropdownHOC, {
  type WithDropdownCmpProps,
  type WithDropdownComponentAddedProps,
  type WithDropdownToggleProps
} from "@common/hocs/withDropdown";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import withDynamicOptionsHOC from "@common/hocs/withDynamicOptions";
import { sort } from "@common/lib/array";
import { useLoadingOptions } from "@common/lib/hooks";
import { identityPromise } from "@common/lib/misc";
import { Common } from "@common/types";
import { default as classnames } from "classnames";
import { identity, isArray, isFunction, isNull, isUndefined, omit } from "lodash";
import * as React from "react";
import { ContentBase, type FooterProps, type SelectHeading, type SortOrder, Toggle } from "../../Base/component";
import withStaticHOC, { type LazySelectComponentProps } from "../../hocs/withStatic";
import { SelectOptionDescription } from "../../SelectOptionDescription/component";
import { type ValueType } from "../lib";
import { getAllState, getSelectAllLabel, getSelectedLabel } from "./lib";

import { useI18n } from "@common/hooks/i18n";
import "./style.less";

/** defines parent item behavior:
 *   group - cannot be selected, not counted as an option
 *   parent - can be selected, not counted as an option
 */
export type MultiSelectGroupType = "group" | "parent";

export type MultiSelectOptionItemBase<T> = Common.LabelValue<T> & {
  children?: MultiSelectOptionItemBase<T>[];
  className?: string;
  descriptions?: OptionItemDescription[];
  group?: string;
  isDisabled?: boolean;
  isSelected: boolean | null;
  parentValue?: T | null;
  sortChildren?: SortOrder;
  tag?: any;
  tooltip?: string;
  type?: MultiSelectGroupType;
};

export type MultiSelectAllCaptions = {
  deselect?: string;
  isNoNumbers?: boolean;
  null?: string;
  select?: string;
};
export type MultiSelectContentProps<T extends ValueType> = Common.WithOptionsFn<MultiSelectOptionItemBase<T>> & {
  allOptions?: MultiSelectOptionItemBase<T>[];
  allCaptions?: MultiSelectAllCaptions;
  footer?: (props: FooterProps) => any;
  keyboardNavigationMode?: KeyboardNavigationMode;
  heading?: SelectHeading | SelectHeading[];
  noValueLabel?: string;
  optionTourStep?: string;
  optionGotoTourStep?: string;
  isSelectAllDisabled?: boolean;
  selectAllHeading?: SelectHeading;
  nullIndicator?: FancyCheckboxNullIndicator;
  nullTooltip?: string;
  onChange: (opt: MultiSelectOptionItemBase<T>, isChecked: boolean) => void;
  onSelectAllClick?: (isChecked: boolean | null) => void;
  renderItemLabel?: (item: MultiSelectOptionItemBase<ValueType>) => any;
  renderItemAddon?: (item: MultiSelectOptionItemBase<ValueType>) => any;
  selected?: number;
  sort?: SortOrder;
  style?: React.CSSProperties;
};

// value can be an array or a string string means a CSV, then onChange will receive a CSV as well
export type MultiSelectBaseProps<T extends ValueType> = MultiSelectContentProps<T> &
  Omit<WithDropdownComponentAddedProps, "align"> & {
    tourStep?: string;
    optionTourStep?: string;
    optionGotoTourStep?: string;
    children?: any;
    className?: string;
    dropdownClassName?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    name?: string;
    placeholder?: string;
    previewSelected?: boolean;
    qa?: string;
    toggle?: any;
  };

const MultiSelectBaseToggle = React.memo(
  <T extends ValueType>(props: WithDropdownToggleProps & MultiSelectBaseProps<T> & LazySelectComponentProps) => {
    const i18n = useI18n();

    const options = React.useMemo(() => {
      if (props.allOptions) return props.allOptions;
      return isFunction(props.options) ? props.options() : props.options;
    }, [props.allOptions, props.options]);

    useLoadingOptions(props.isLoading ?? false, options);

    const di = React.useMemo(() => {
      if (!isUndefined(props.placeholder)) return { placeholder: props.placeholder, title: props.placeholder };

      if (props.previewSelected) {
        // TODO: better approach
        const options = isFunction(props.options) ? props.options() : props.options;
        // TODO: > 1 level handling
        const candidates = options.filter((x) => x.isSelected || x.children?.some((x) => x.isSelected));
        if (candidates.length) {
          return {
            placeholder: candidates
              .slice(0, 10)
              .map((x) => x.label)
              .join(", "),
            title: candidates.map((x) => x.label).join(", ")
          };
        }
      }
    }, [props.previewSelected, props.options, props.placeholder]);

    return (
      <Toggle
        {...props}
        htmlTitle={di?.title}
        isNoOptions={!options.length && props.loadMore === identityPromise}
        onClick={
          props.isLoading ||
          props.isDisabled ||
          (!options.length && (Array.isArray(props.heading) ? !props.heading.length : !props.heading))
            ? undefined
            : props.toggleOpen
        }
        title={di?.placeholder ?? getSelectedLabel(i18n, props, props.noValueLabel)}
      />
    );
  }
);

const MultiSelectBaseContent = <T extends ValueType>(
  props: WithDropdownCmpProps & MultiSelectContentProps<T> & LazySelectComponentProps
) => {
  const i18n = useI18n();

  const [expanded, setExpanded] = React.useState<Common.GenericMap<null>>({});

  const options = React.useMemo(() => (isFunction(props.options) ? props.options() : props.options), [props.options]);

  const headings = React.useMemo(() => {
    const headingsArray: SelectHeading[] = [];
    if (props.heading) headingsArray.push(...(isArray(props.heading) ? props.heading : [props.heading]));

    if (props.loadMore === identityPromise) {
      if (props.selectAllHeading) {
        headingsArray.push(props.selectAllHeading);
      } else if (props.onSelectAllClick && !!options.length) {
        headingsArray.push({
          content: (
            <FancyCheckbox
              isDisabled={props.isSelectAllDisabled}
              isThreeState={!!props.nullIndicator}
              labelClassName="single-line-ellipsis"
              label={getSelectAllLabel(i18n, props)}
              nullIndicator={props.nullIndicator}
              value={getAllState(props)}
              onChange={props.onSelectAllClick}
            />
          ),
          isSearch: false,
          isToggle: false
        });
      }
    }

    return headingsArray;
  }, [
    props.heading,
    props.selectAllHeading,
    props.onSelectAllClick,
    options,
    props.isSelectAllDisabled,
    props.nullIndicator,
    props.loadMore
  ]);

  const renderItems = React.useCallback(
    (options: MultiSelectOptionItemBase<T>[], level: number, sortOrder?: SortOrder) => {
      const finalItems = sortOrder
        ? sort(options, {
            key: "label",
            order: sortOrder,
            type: "natural"
          })
        : options;

      const groups: GenericMap<string> = finalItems.reduce((res, x) => {
        if (x.group && !res[x.group]) {
          res[x.group] = x.value;
        }
        return res;
      }, {});

      return finalItems.map((opt) => (
        <React.Fragment key={opt.value}>
          {!!opt.group && groups[opt.group] === opt.value && Object.keys(groups).length > 1 && (
            <div className="group">
              <div className="group_content">
                <Hint className="caption">{opt.group}</Hint>
              </div>
            </div>
          )}

          <div
            className={classnames("item", opt.className)}
            onClick={(e) => {
              e.stopPropagation();
              if (opt.isDisabled || opt.type === "group") {
                if (opt.children?.length) {
                  toggleExpanded(opt.value)(e);
                }
              } else {
                props.onChange(opt, !opt.isSelected);
              }
            }}
            {...getTourAttributes({ step: props.optionTourStep, goToStep: props.optionGotoTourStep })}
          >
            <div className="item-content" data-name={opt.value ?? ""} style={{ "--level": level } as any}>
              <FancyCheckbox
                isDisabled={opt.isDisabled}
                label={
                  <>
                    {props.renderItemLabel ? props.renderItemLabel(opt) : opt.label}{" "}
                    {opt.descriptions ? <SelectOptionDescription descriptions={opt.descriptions} isSingleLine /> : null}
                  </>
                }
                htmlValue={opt.value}
                nullIndicator={props.nullIndicator}
                onChange={identity}
                tooltip={isNull(opt.isSelected) ? props.nullTooltip : undefined}
                value={opt.isSelected}
              />

              {props.renderItemAddon && props.renderItemAddon(opt)}

              {opt.children?.length ? (
                <Icon
                  className="toggle"
                  icon={opt.value in expanded ? Common.IconType.ChevronUp : Common.IconType.ChevronDown}
                  onClick={toggleExpanded(opt.value)}
                />
              ) : null}
            </div>
          </div>
          {opt.children && opt.value in expanded && (
            <Collapsible className="children" isCollapsed={!(opt.value in expanded)} isLazy>
              {renderItems(opt.children!, level + 1, opt.sortChildren)}
            </Collapsible>
          )}
        </React.Fragment>
      ));
    },
    [expanded, props.onChange, props.renderItemLabel, props.renderItemAddon, props.nullIndicator, props.nullTooltip]
  );

  const toggleExpanded = React.useCallback(
    (value: T) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setExpanded((prevState) => (value in prevState ? omit(prevState, value) : { ...prevState, [value]: null }));
    },
    []
  );

  return (
    <ContentBase
      className="multi_select"
      close={close}
      footer={props.footer}
      infiniteLoaderKey={props.infiniteLoaderKey}
      keyboardNavigationMode={props.keyboardNavigationMode}
      hasMore={props.hasMore}
      heading={headings}
      isOpen={props.isOpen}
      loadMore={props.loadMore}
      open={open}
      options={options}
      tag="div"
      toggleOpen={props.toggleOpen}
    >
      {renderItems(options, 1, props.sort)}
    </ContentBase>
  );
};

const MultiSelectBaseWithDropdown = withDropdownHOC<MultiSelectBaseProps<ValueType>>(
  MultiSelectBaseToggle,
  MultiSelectBaseContent
);

export const MultiSelectBaseLazy = React.memo(
  <T extends ValueType>({
    dropdownClassName,
    ...props
  }: Omit<MultiSelectBaseProps<T> & LazySelectComponentProps & WithDropdownComponentAddedProps, "align">) => (
    <MultiSelectBaseWithDropdown
      {...props}
      align={ContentAlign.Stretch}
      dropdownClassName={classnames("select_dropdown", dropdownClassName)}
      dropdownName={props.qa || props.name ? `${props.qa || props.name}_dropdown` : undefined}
    />
  )
);

const MultiSelectBase = withStaticHOC(MultiSelectBaseLazy);

export default MultiSelectBase;

const DynamicMultiSelectBase = withDynamicOptionsHOC<any, MultiSelectBaseProps<ValueType>>(MultiSelectBase);
export { DynamicMultiSelectBase };
